/** A status as a chip: colour AND an icon AND a word, never colour alone (design rule 4, F-040).
 *
 * Each tone has its own Phosphor glyph, so a reader who cannot tell the amber from the red still gets
 * a different shape and a label. The label is required and is the chip's accessible name; the icon
 * is decoration. It states a fact and is not a control: for something pressable use a button.
 *
 * **One chip, in sentence case** (design uplift U4). The words are wrapped so the first letter can be
 * capitalised by the stylesheet (`.text::first-letter`): a server state such as "declared nil" or
 * "missing" reads "Declared nil" and "Missing" without the text node changing, so a test or a screen
 * reader sees what the caller wrote. Nothing is upper-cased past the first letter, and no chip is
 * letter-spaced; `StatusPill` is this component.
 */

import { CheckCircle, Info, MinusCircle, WarningCircle, XCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import styles from "./StatusChip.module.css";

export type ChipTone = "crit" | "warn" | "ok" | "info" | "neutral";

const ICON: Record<ChipTone, Icon> = {
  crit: XCircle,
  warn: WarningCircle,
  ok: CheckCircle,
  info: Info,
  neutral: MinusCircle,
};

export interface StatusChipProps {
  tone: ChipTone;
  /** The words. Required: a chip with only a colour is the defect this exists to prevent. */
  children: ReactNode;
  size?: "sm" | "md";
  /** Replace the tone's default glyph (still decorative). */
  icon?: Icon;
  testId?: string;
  title?: string;
}

export function StatusChip({ tone, children, size = "md", icon, testId, title }: StatusChipProps) {
  const Glyph = icon ?? ICON[tone];
  return (
    <span
      className={`${styles.chip} ${styles[tone]} ${size === "sm" ? styles.sm : styles.md}`}
      data-tone={tone}
      data-size={size}
      data-testid={testId}
      title={title}
    >
      <Glyph size={size === "sm" ? 12 : 14} weight="bold" aria-hidden="true" className={styles.icon} />
      <span className={styles.text}>{children}</span>
    </span>
  );
}
